<template>
  <div style="overflow-x: auto;">
    <div class="mb-1 text-right">
      <b-button
        variant="primary"
        size="sm"
        @click="openAssignedRequestsModal"
      >
        <feather-icon
          icon="ListIcon"
          class="mr-50"
        />
        View Assigned Requests
      </b-button>

      <b-button
        variant="outline-primary"
        size="sm"
        class="ml-50"
        @click="openCertificatesModal"
      >
        <feather-icon
          icon="AwardIcon"
          class="mr-50"
        />
        View Certificates
      </b-button>
    </div>

    <!-- Certificates covered by this evaluation's certificate category -->
    <b-modal
      v-model="showCertificatesModal"
      :title="certificatesModalTitle"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <div
        v-if="loadingCertificates"
        class="text-center my-2"
      >
        <b-spinner label="Loading..." />
      </div>
      <p
        v-else-if="!categoryCertificates.length"
        class="text-muted mb-0"
      >
        No certificates found for this category.
      </p>
      <b-list-group
        v-else
        flush
      >
        <b-list-group-item
          v-for="cert in categoryCertificates"
          :key="cert.id"
          class="d-flex justify-content-between align-items-center"
        >
          <span>{{ cert.label || cert.name }}</span>
          <b-badge
            :variant="cert.open_applications > 0 ? 'light-primary' : 'light-secondary'"
            class="ml-1 flex-shrink-0"
            :style="cert.open_applications > 0 ? 'cursor: pointer;' : ''"
            @click="cert.open_applications > 0 && openApplicationsModal(cert)"
          >
            {{ cert.open_applications || 0 }} open
          </b-badge>
        </b-list-group-item>
      </b-list-group>
    </b-modal>

    <!-- The open applications behind one certificate -->
    <b-modal
      v-model="showApplicationsModal"
      :title="applicationsModalTitle"
      size="xl"
      ok-only
      ok-title="Close"
    >
      <div
        v-if="loadingApplications"
        class="text-center my-2"
      >
        <b-spinner label="Loading..." />
      </div>
      <p
        v-else-if="!openApplications.length"
        class="text-muted mb-0"
      >
        No open applications.
      </p>
      <b-table
        v-else
        :items="openApplications"
        :fields="applicationFields"
        responsive
        small
        striped
      >
        <template #cell(assigned)="data">
          <span
            v-if="!data.item.assigned.length"
            class="text-muted"
          >-</span>
          <div v-else>
            <div
              v-for="(person, i) in data.item.assigned"
              :key="i"
            >
              {{ person }}
            </div>
          </div>
        </template>
      </b-table>
    </b-modal>

    <PersonnelGeneralTable
      :items-user-data="userOpenData"
      :ex-evaluation-date="exEvaluationDate"
      :fields-user-data="fields"
      @changePersonneleEvaluation="changePersonneleEvaluation"
    />
    <edit-evaluator-and-activity
      :data-in-modal="dataInModal"
      @close="dataInModal.show = false"
    />

    <!-- Modal for Assigned Requests Reference List -->
    <b-modal
      v-model="showAssignedModal"
      title="Assigned Requests Reference List"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="loadingAssignedRequests"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
        <p class="mt-1 text-muted">
          Loading assigned requests...
        </p>
      </div>

      <div
        v-else-if="!assignedRequests.length"
        class="text-center py-3"
      >
        <p class="text-muted mb-0">
          No assigned requests found for this engineer in this category.
        </p>
      </div>

      <div v-else>
        <b-table
          :items="assignedRequests"
          :fields="assignedRequestsFields"
          responsive
          striped
          hover
          bordered
          small
        >
          <template #cell(request_code)="data">
            <a
              :href="getCeUrl(data.item.id)"
              target="_blank"
              rel="noopener noreferrer"
              class="font-weight-bold text-primary"
            >
              {{ data.item.request_code }}
            </a>
          </template>

          <template #cell(client_name)="data">
            <span>{{ data.item.client_name || '-' }}</span>
          </template>

          <template #cell(application_date)="data">
            <span>{{ formatDate(data.item.application_date) }}</span>
          </template>

          <template #cell(certificate_number)="data">
            <span
              v-if="isTerminated(data.item.status)"
              class="badge badge-light-success"
            >
              {{ data.item.certificate_number || '-' }}
            </span>
            <span
              v-else
              class="text-muted small"
            >
              -
            </span>
          </template>

          <template #cell(certificate_issued_at)="data">
            <span v-if="isTerminated(data.item.status)">
              {{ formatDate(data.item.certificate_issued_at) }}
            </span>
            <span
              v-else
              class="text-muted small"
            >
              -
            </span>
          </template>
        </b-table>
      </div>

      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          size="sm"
          @click="showAssignedModal = false"
        >
          Close
        </b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
import axios from 'axios'
import BaseUrl from '@/libs/base-url'
import decryptData from '@/store/auth/decryptData'
import PersonnelGeneralTable from './PersonnelGeneralTable.vue'
import EditEvaluatorAndActivity from '../personnel-action/EditEvaluatorAndActivity.vue'

export default {
  components: {
    EditEvaluatorAndActivity,
    PersonnelGeneralTable,
  },
  props: {
    userOpenData: {
      type: Array,
      default: () => [],
    },
    exEvaluationDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fields: [
        { key: 'ce_level_before.status', label: 'Ce Level Before' },
        { key: 'ex__date', label: 'Expected Date' },
        { key: 'evaluator_name', label: 'Evaluators' },
        { key: 'activity', label: 'activity' },
        // { key: 'personnel_evaluation_remarks', label: 'Remarks' },
      ],
      dataInModal: {
        show: false,
        id: '',
        type: '',
        certificate_category_id: null,
      },
      showAssignedModal: false,
      loadingAssignedRequests: false,
      assignedRequests: [],
      showCertificatesModal: false,
      loadingCertificates: false,
      categoryCertificates: [],
      showApplicationsModal: false,
      loadingApplications: false,
      openApplications: [],
      applicationsCertificate: null,
      applicationsTotal: 0,
      applicationFields: [
        { key: 'request_code', label: 'Application No.' },
        { key: 'external_status', label: 'External Status' },
        { key: 'internal_status', label: 'Internal Status' },
        { key: 'client', label: 'Client' },
        { key: 'assigned', label: 'Assigned To' },
        { key: 'created_at', label: 'Created' },
      ],
      assignedRequestsFields: [
        { key: 'request_code', label: 'Application Number' },
        { key: 'client_name', label: 'Client Name' },
        { key: 'application_date', label: 'Application Date' },
        { key: 'certificate_number', label: 'Certificate Number' },
        { key: 'certificate_issued_at', label: 'Certificate Issue Date' },
      ],
    }
  },
  computed: {
    applicationsModalTitle() {
      const label = this.applicationsCertificate?.label || 'Certificate'
      const shown = this.openApplications.length
      const more = this.applicationsTotal > shown ? ` (showing ${shown} of ${this.applicationsTotal})` : ''
      return `Open Applications - ${label}${more}`
    },
    certificatesModalTitle() {
      const open = this.categoryCertificates.reduce((sum, c) => sum + (c.open_applications || 0), 0)
      return `Certificates in this Category — ${this.categoryCertificates.length} certificates, ${open} open applications`
    },
    getExDate() {
      const formatDate = str => {
        if (!str) return ''
        const date = new Date(str)
        return date.toLocaleDateString('en-GB')
      }

      return formatDate(this.exEvaluationDate)
    },
  },

  methods: {
    changePersonneleEvaluation(id, type, cerId) {
      this.dataInModal = {
        show: true,
        id,
        type,
        certificate_category_id: cerId,
      }
    },
    getCeUrl(requestId) {
      const role = localStorage.getItem('accessRole') || ''
      const token = localStorage.getItem('accessToken') || ''
      const name = localStorage.getItem('name') || ''
      const email = localStorage.getItem('email') || ''
      return `${BaseUrl.linkToEngineers}/${requestId}?role=${role}&name=${name}&email=${email}&token=${token}`
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    async openApplicationsModal(certificate) {
      this.applicationsCertificate = certificate
      this.showApplicationsModal = true
      this.loadingApplications = true
      this.openApplications = []
      this.applicationsTotal = 0

      // A row can stand for several certificates sharing a name, so send every id behind it.
      const ids = (certificate.ids && certificate.ids.length) ? certificate.ids : [certificate.id]

      try {
        const res = await axios.get('personnel-evaluation/certificates/open-applications', {
          params: { certificate_ids: ids.join(',') },
        })
        const payload = decryptData(res.data.payload)?.data || {}
        this.openApplications = payload.requests || []
        this.applicationsTotal = payload.total || this.openApplications.length
      } catch (error) {
        this.openApplications = []
      } finally {
        this.loadingApplications = false
      }
    },
    async openCertificatesModal() {
      this.showCertificatesModal = true
      this.loadingCertificates = true
      this.categoryCertificates = []

      const firstItem = this.userOpenData[0] || {}
      const categoryId = firstItem.certificate_category_id || firstItem.certificate_category?.id

      if (!categoryId) {
        this.loadingCertificates = false
        return
      }

      try {
        const res = await axios.get(`personnel-evaluation/certificate-category/${categoryId}/certificates`)
        this.categoryCertificates = decryptData(res.data.payload)?.data?.certificates || []
      } catch (error) {
        this.categoryCertificates = []
      } finally {
        this.loadingCertificates = false
      }
    },
    async openAssignedRequestsModal() {
      this.showAssignedModal = true
      this.loadingAssignedRequests = true
      this.assignedRequests = []

      const firstItem = this.userOpenData[0] || {}
      const userId = firstItem.user_id || this.$route.params.id
      const categoryId = firstItem.certificate_category_id || firstItem.certificate_category?.id

      if (!userId || !categoryId) {
        this.loadingAssignedRequests = false
        return
      }

      try {
        const res = await axios.get('personnel-evaluation/assigned-requests', {
          params: {
            user_id: userId,
            certificate_category_id: categoryId,
          },
        })
        const decrypted = decryptData(res.data.payload)
        this.assignedRequests = decrypted.data || []
      } catch (error) {
        // handle fetch error silently
      } finally {
        this.loadingAssignedRequests = false
      }
    },
    isTerminated(status) {
      if (!status) return false
      const s = String(status).toLowerCase().trim()
      return s === 'terminated' || s === 'completed' || s === 'issued' || s.includes('terminated') || s.includes('issued')
    },
  },
}
</script>
